import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <a
              href="/"
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
            >
              <span className="text-xl md:text-2xl font-heading font-bold text-foreground-900 tracking-tight">
                Kakobuy<span className="text-primary-500">Finds</span>
              </span>
            </a>

            <div className="flex items-center gap-3 md:gap-4">
              <div className="relative hidden md:block" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-foreground-200/60 bg-background-50/80 text-sm text-foreground-700 hover:border-foreground-300 transition-all duration-200 cursor-pointer whitespace-nowrap font-medium"
                >
                  <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                  <span className="hidden sm:inline">{t('category.select')}</span>
                  <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 overflow-hidden max-h-[420px] overflow-y-auto">
                    <div className="p-2">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-500`}></i>
                          <span>{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-smile-line w-4 h-4 flex items-center justify-center"></i>
                <span>kakobuy sheet</span>
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-full border border-foreground-200/60 bg-background-50/80 text-foreground-700 hover:border-foreground-300 transition-all duration-200 cursor-pointer"
                aria-label="Menu"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} w-5 h-5 flex items-center justify-center`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-900/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          <div
            ref={mobileMenuRef}
            className="absolute right-0 top-0 bottom-0 w-[320px] max-w-[85vw] bg-background-50 overflow-y-auto animate-slide-in"
          >
            <div className="p-6 pt-20">
              <div className="mb-6">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap mb-3"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-user-smile-line w-4 h-4 flex items-center justify-center"></i>
                  <span>kakobuy sheet</span>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full border-2 border-foreground-900 text-foreground-900 text-sm font-semibold hover:bg-foreground-900 hover:text-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
                  <span>buy it now</span>
                </a>
              </div>

              <div className="mb-6">
                <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3 px-1">
                  Kategorie produktów
                </h3>
                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-600`}></i>
                      </div>
                      <span className="font-medium">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="border-t border-background-200/70 pt-6">
                <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3 px-1">
                  Poradniki SEO
                </h3>
                <div className="space-y-0.5">
                  <button
                    onClick={() => { navigate('/artykul/kakobuy-sheet-kompletny-przewodnik'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-book-open-line w-4 h-4 flex items-center justify-center text-primary-600"></i>
                    </div>
                    <span className="font-medium">Pełny Przewodnik</span>
                  </button>
                  <button
                    onClick={() => { navigate('/artykul/porownanie-spreadsheet-zakupowych'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-scales-line w-4 h-4 flex items-center justify-center text-secondary-600"></i>
                    </div>
                    <span className="font-medium">Porównanie Platform</span>
                  </button>
                  <button
                    onClick={() => { navigate('/artykul/bezpieczne-korzystanie-ze-spreadsheet'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center text-accent-600"></i>
                    </div>
                    <span className="font-medium">Bezpieczeństwo</span>
                  </button>
                  <button
                    onClick={() => { navigate('/artykul/kakobuy-spreadsheet-sneakersy-buty'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-footprint-line w-4 h-4 flex items-center justify-center text-foreground-600"></i>
                    </div>
                    <span className="font-medium">Sneakersy i Buty</span>
                  </button>
                  <button
                    onClick={() => { navigate('/artykul/kakobuy-spreadsheet-moda-odziez'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-t-shirt-line w-4 h-4 flex items-center justify-center text-foreground-600"></i>
                    </div>
                    <span className="font-medium">Moda i Odzież</span>
                  </button>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-background-200/70">
                <a
                  href="/"
                  className="flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-150 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/');
                    setMobileMenuOpen(false);
                  }}
                >
                  <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                  Strona główna
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
}